import TilesetStylingPlugin, {
  StyleRule,
  TilesetStylePriority,
} from "./TilesetStylingPlugin";
import { deferred, Guard } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import { Viewer } from "cesium";
import TilesetPlugin from "./TilesetPlugin";
import FeatureRayPickerPlugin from "./FeatureRayPickerPlugin";
import { paintRollerCursor } from "../utils/paintCursors";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

export type PaintTool = "spray" | "bucket" | "erase";

type PaintedInstance = {
  tilesetId: string;
  elementId: string | null;
  color: string;
};

export type PaintToolSnapshot = {
  version: 1;
  painted: PaintedInstance[];
};

export function isPaintStateApplied(
  state: PaintToolSnapshot | undefined,
): boolean {
  return (state?.painted?.length ?? 0) > 0;
}

export default class PaintToolPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<PaintToolSnapshot>,
    BackgroundableToolInterface
{
  static readonly DEFAULT_TOOL: PaintTool = "spray";
  static readonly DEFAULT_COLOR = "#22C55E"; //green

  private _viewer = deferred<Viewer>();
  private _isActive = false;
  private _detachClickHandlerFunc: () => void;
  private _detachHoverHandlersFunc: (() => void) | null = null;
  /** Held directly (the viewer itself is only available asynchronously) to set the hover cursor. */
  private _canvas: HTMLCanvasElement | null = null;
  private _isOverPaintableElement = false;

  private readonly _painted = new ObservableProperty<PaintedInstance[]>([]);
  private readonly _activeTool = new ObservableProperty(
    PaintToolPlugin.DEFAULT_TOOL,
  );
  private readonly _activeColor = new ObservableProperty(
    PaintToolPlugin.DEFAULT_COLOR,
  );
  readonly isApplied = new DerivedObservableProperty(
    () => isPaintStateApplied(this.capture()),
    [this._painted],
  );

  get painted(): ReadonlyObservableProperty<PaintedInstance[]> {
    return this._painted;
  }

  get activeTool(): ReadonlyObservableProperty<PaintTool> {
    return this._activeTool;
  }

  get activeColor(): ReadonlyObservableProperty<string> {
    return this._activeColor;
  }

  constructor(
    private readonly _tilesetStylingPlugin: TilesetStylingPlugin,
    private readonly _featureRayPickerPlugin: FeatureRayPickerPlugin,
  ) {}

  setViewer(viewer: Viewer): void {
    this._canvas = viewer.canvas;
    this._viewer.ready(viewer);
  }

  detachViewer(): void {
    this._isActive = false;
    this.setInteractiveMode(false);
    this._canvas = null;
    this.clear();
    this._activeTool.set(PaintToolPlugin.DEFAULT_TOOL);
    this._activeColor.set(PaintToolPlugin.DEFAULT_COLOR);

    this._viewer.dispose();
    this._viewer = deferred();
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this._isActive = true;
    this.setInteractiveMode(true);
  }

  deactivate(): void {
    this._isActive = false;
    this.setInteractiveMode(false);
  }

  restore(state: PaintToolSnapshot | undefined): void {
    if (!state) return;

    this.removeStylingRules();
    const painted = state.painted ? [...state.painted] : [];
    this._painted.set(painted);
    this.applyStylingRules();
  }

  capture(): PaintToolSnapshot {
    return {
      version: 1,
      painted: [...this._painted.getSnapshot()],
    };
  }

  isStateApplied(state: PaintToolSnapshot | undefined): boolean {
    return isPaintStateApplied(state);
  }

  isMatchingState(state: PaintToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  setInteractiveMode(isEnabled: boolean) {
    if (isEnabled) {
      this.attachLeftClickHandler();
      this.attachHoverCursorHandlers();
    } else {
      this._detachClickHandlerFunc?.();
      this._detachClickHandlerFunc = null;
      this._detachHoverHandlersFunc?.();
      this._detachHoverHandlersFunc = null;
      this.setPaintableCursor(false);
    }
  }

  /** Shows the paint roller cursor while hovering an element a click would change. */
  private attachHoverCursorHandlers() {
    this._detachHoverHandlersFunc?.();
    const detachFeatureHandler =
      this._featureRayPickerPlugin.mouseMoveFeatureEvent.addHandler(() =>
        this.setPaintableCursor(true),
      );
    const detachEmptyHandler =
      this._featureRayPickerPlugin.mouseMoveEmptyEvent.addHandler(() =>
        this.setPaintableCursor(false),
      );
    this._detachHoverHandlersFunc = () => {
      detachFeatureHandler();
      detachEmptyHandler();
    };
  }

  /** Paint roller while colouring; the standard hand while removing colour. */
  private setPaintableCursor(isOverPaintableElement: boolean) {
    this._isOverPaintableElement = isOverPaintableElement;
    if (!this._canvas) return;
    if (!isOverPaintableElement) {
      this._canvas.style.cursor = "";
      return;
    }
    this._canvas.style.cursor =
      this._activeTool.getSnapshot() === "erase"
        ? "pointer"
        : paintRollerCursor;
  }

  setActiveColor(color: string) {
    Guard.require(color, "color");
    this._activeColor.set(color);
  }

  setActiveTool(tool: PaintTool) {
    Guard.require(tool, "tool");
    this._activeTool.set(tool);
    // Swap the cursor straight away if it's already over an element.
    if (this._isOverPaintableElement) this.setPaintableCursor(true);
  }

  clear() {
    this.removeStylingRules();
    this._painted.set([]);
    this.applyStylingRules();
  }

  paintElement(elementId: string, tilesetId: string) {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");
    this.removeStylingRules();
    const tool = this._activeTool.getSnapshot();
    const color = this._activeColor.getSnapshot();
    if (tool === "spray") {
      this.sprayElement(elementId, tilesetId, color);
    } else if (tool === "bucket") {
      this.bucketElement(tilesetId, color);
    } else if (tool === "erase") {
      this.eraseElement(elementId, tilesetId);
    }
    this.applyStylingRules();
  }

  private sprayElement(elementId: string, tilesetId: string, color: string) {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");

    function isThisElement(instance: PaintedInstance) {
      return (
        instance.tilesetId === tilesetId && instance.elementId === elementId
      );
    }
    function isWholeTilesetThisColor(instance: PaintedInstance) {
      return (
        instance.tilesetId === tilesetId &&
        instance.elementId === null &&
        instance.color === color
      );
    }

    const painted = this._painted.getSnapshot().filter(not(isThisElement));
    if (!painted.find(isWholeTilesetThisColor)) {
      painted.push({
        tilesetId,
        elementId,
        color,
      });
    }
    this._painted.set(painted);
  }

  private bucketElement(tilesetId: string, color: string) {
    Guard.require(tilesetId, "tilesetId");

    function isElementSpecificWithThisColor(instance: PaintedInstance) {
      return (
        instance.tilesetId === tilesetId &&
        instance.elementId !== null &&
        instance.color === color
      );
    }
    function isWholeTileset(instance: PaintedInstance) {
      return instance.tilesetId === tilesetId && instance.elementId === null;
    }

    const painted = this._painted
      .getSnapshot()
      .filter(not(isWholeTileset))
      .filter(not(isElementSpecificWithThisColor));
    painted.push({
      tilesetId,
      elementId: null,
      color,
    });
    this._painted.set(painted);
  }

  private eraseElement(elementId: string, tilesetId: string) {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");

    function isSameElement(instance: PaintedInstance) {
      return (
        instance.tilesetId === tilesetId && instance.elementId === elementId
      );
    }
    function isWholeTileset(instance: PaintedInstance) {
      return instance.tilesetId === tilesetId && instance.elementId === null;
    }

    let painted = this._painted.getSnapshot();
    const originalLength = painted.length;
    painted = painted.filter(not(isSameElement));
    if (painted.length === originalLength) {
      painted = painted.filter(not(isWholeTileset));
    }
    this._painted.set(painted);
  }

  private attachLeftClickHandler() {
    this._detachClickHandlerFunc?.();
    this._detachClickHandlerFunc =
      this._featureRayPickerPlugin.leftClickFeatureEvent.addHandler(
        (object) => {
          if (!TilesetPlugin.isFeature(object)) return;
          const feature = object;

          const elementId = TilesetPlugin.getElementIdFromFeature(feature);
          const tilesetId = TilesetPlugin.getTilesetIdFromTileset(
            feature.tileset,
          );
          this.paintElement(elementId, tilesetId);
        },
      );
  }

  private removeStylingRules() {
    for (const painted of this._painted.getSnapshot()) {
      let rule: StyleRule;
      if (painted.elementId !== null) {
        rule = {
          priority: TilesetStylePriority.PaintToolElement,
          elementStyle: {
            tilesetId: painted.tilesetId,
            elementId: painted.elementId,
          },
        };
      } else {
        rule = {
          priority: TilesetStylePriority.PaintToolTileset,
          tilesetStyle: {
            tilesetId: painted.tilesetId,
          },
        };
      }
      this._tilesetStylingPlugin.removeRule(rule);
    }
  }

  private async applyStylingRules() {
    for (const painted of this._painted.getSnapshot()) {
      let rule: StyleRule;
      if (painted.elementId !== null) {
        rule = {
          priority: TilesetStylePriority.PaintToolElement,
          elementStyle: {
            tilesetId: painted.tilesetId,
            elementId: painted.elementId,
            color: painted.color,
          },
        };
      } else {
        rule = {
          priority: TilesetStylePriority.PaintToolTileset,
          tilesetStyle: {
            tilesetId: painted.tilesetId,
            color: painted.color,
          },
        };
      }
      this._tilesetStylingPlugin.setRule(rule);
    }
    await this._tilesetStylingPlugin.applyRules();
  }
}

function not(fn: (instance: PaintedInstance) => boolean) {
  return (instance: PaintedInstance) => !fn(instance);
}
